import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField } from "@mui/material";
import * as Yup from "yup";
import { debounce } from "lodash";
// @ts-expect-error: ignoring as it is existing js file
import { isValidInput } from "@/components/Utils/helperFunction";
import {
  updateLoanDetails,
  loanDetailsSelector,
  saveLoanDetails,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type { ApplicationFormSlice, LoanDetails } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
// @ts-expect-error: ignoring as it is existing js file
import { NumericFormatCustom, currencyMaskedValue } from "@/utils/currencyMaskFormat";

interface RequestedLoanAmountProps {
  applicationId: string;
  quoteValues: Record<string, string>;
  setQuoteValue: Dispatch<SetStateAction<Record<string, string>>>;
  fieldErrors: Record<string, string>;
  setFieldErrors: Dispatch<SetStateAction<Record<string, string>>>;
  disabled?: boolean;
}

const RequestedLoanAmount = ({
  applicationId,
  quoteValues,
  setQuoteValue,
  fieldErrors,
  setFieldErrors,
  disabled,
}: RequestedLoanAmountProps) => {
  const dispatch = useDispatch();
  const { requestedLoanAmount, _id } = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const [loanAmount, setLoanAmount] = useState(0);
  const validationSchema = Yup.object({
    requestedLoanAmount: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
  });

  const debouncedValidation = useCallback(
    debounce(async (value: string, fieldName: string) => {
      try {
        await validationSchema.validateAt(fieldName, { [fieldName]: value });
        setFieldErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          setFieldErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: error.message,
          }));
        }
      }
    }, 300),
    [],
  );

  const onChange = (value: string, fieldName: string) => {
    let isValid = true;
    if (value === "") {
      setQuoteValue({ ...quoteValues, [fieldName]: value });
      setFieldErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
      return;
    }
    if (fieldName === "requestedLoanAmount") {
      isValid = isValidInput(value);
    }
    if (!isValid) {
      debouncedValidation(value, fieldName);
      return;
    } else {
      setQuoteValue({ ...quoteValues, [fieldName]: value });
      debouncedValidation(value, fieldName);
    }
  };

  const onBlur = async (value: string, fieldName: string) => {
    try {
      if (fieldErrors[fieldName] === "") {
        dispatch(saveLoanDetails({ [fieldName]: value }));
        if (_id && Number(value) !== Number(requestedLoanAmount)) {
          dispatch(updateLoanDetails({ applicationId, _id, [fieldName]: value }));
        }
      }
    } catch (error) {
      const newErrors = {};
      // @ts-expect-error: ignoring as it is existing code. Will look when refactoring.
      error.inner.forEach((validationError) => {
        // @ts-expect-error: ignoring as it is existing code. Will look when refactoring.
        newErrors[validationError.path] = validationError.message;
      });
      setFieldErrors(newErrors);
    }
  };

  // Set requested loan amount saved in loan details into local state.
  useEffect(() => {
    setLoanAmount(requestedLoanAmount || 0);
  }, [requestedLoanAmount]);

  return (
    <TextField
      fullWidth
      id="requestedLoanAmount"
      label="Requested loan amount"
      variant="filled"
      type="text"
      size="small"
      name="requestedLoanAmount"
      value={loanAmount}
      error={!!fieldErrors?.requestedLoanAmount}
      helperText={fieldErrors?.requestedLoanAmount}
      onChange={(event) => onChange(event?.target?.value, "requestedLoanAmount")}
      onBlur={(event) => onBlur(currencyMaskedValue(event?.target?.value), "requestedLoanAmount")}
      InputProps={{
        inputComponent: NumericFormatCustom,
      }}
      disabled={disabled}
    />
  );
};

export { RequestedLoanAmount };
